import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useWorkspaces } from "../hooks/useWorkspaces";
import { apiFetch } from "../services/api";
import { showToast } from "../components/UI";

export default function WorkspaceSettingsPage() {
  const navigate = useNavigate();
  const { activeWorkspace, refetch } = useWorkspaces();

  const [name, setName]             = useState("");
  const [saving, setSaving]         = useState(false);
  const [error, setError]           = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [saved, setSaved]           = useState(false);

  useEffect(() => {
    if (activeWorkspace?.name) setName(activeWorkspace.name);
  }, [activeWorkspace?.name]);

  async function handleRename(e) {
    e.preventDefault();
    if (!activeWorkspace) return;
    const trimmed = name.trim();
    if (!trimmed || trimmed === activeWorkspace.name) return;

    setSaving(true);
    setError("");
    setSuggestions([]);
    setSaved(false);

    try {
      await apiFetch(`/api/workspaces/${activeWorkspace.id}/rename`, {
        method: "PATCH",
        body: JSON.stringify({ name: trimmed }),
      });
      await refetch();
      setSaved(true);
      showToast("Business name updated.");
      setTimeout(() => setSaved(false), 3000);
    } catch (e) {
      // 409 = name taken, detail contains suggestions
      try {
        const body = JSON.parse(e.message.replace("API error 409: ", "").replace(/^API error \d+$/, "{}"));
        setError(body.message || "That name is already taken.");
        setSuggestions(body.suggestions || []);
      } catch {
        setError(e.message || "Failed to rename. Please try again.");
      }
    } finally {
      setSaving(false);
    }
  }

  function pickSuggestion(s) {
    // Convert slug back to title case name
    const suggested = s.split("-").map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
    setName(suggested);
    setError("");
    setSuggestions([]);
  }

  const unchanged = name.trim() === (activeWorkspace?.name || "");

  return (
    <div style={s.root}>
      {/* Top nav */}
      <header style={s.header}>
        <Link to="/dashboard" style={s.logo}>
          raji<span style={{ color: "var(--accent)" }}>.</span>ai
        </Link>
        <span style={s.breadcrumb}>
          <Link to="/dashboard" style={s.breadLink}>Dashboard</Link>
          {" / "}
          <span style={{ color: "var(--ink-mute)" }}>Workspace Settings</span>
        </span>
      </header>

      <div style={s.body}>
        <div style={s.card}>
          <h1 style={s.heading}>Workspace Settings</h1>
          <p style={s.sub}>Manage your business details.</p>

          <hr style={s.divider} />

          {/* Rename section */}
          <section>
            <h2 style={s.sectionTitle}>Business Name</h2>
            <p style={s.sectionDesc}>
              This name appears across your dashboard and public site. The URL slug updates automatically.
            </p>

            <form onSubmit={handleRename} style={s.form}>
              <div style={s.inputRow}>
                <input
                  style={{
                    ...s.input,
                    borderColor: error
                      ? "rgba(224,112,112,0.5)"
                      : saved
                      ? "rgba(93,202,165,0.5)"
                      : "var(--paper-3)",
                  }}
                  value={name}
                  onChange={e => { setName(e.target.value); setError(""); setSuggestions([]); setSaved(false); }}
                  placeholder="Your business name"
                  maxLength={64}
                  disabled={saving}
                />
                <button
                  type="submit"
                  disabled={saving || unchanged}
                  style={{
                    ...s.saveBtn,
                    opacity: saving || unchanged ? 0.4 : 1,
                    cursor: saving || unchanged ? "not-allowed" : "pointer",
                  }}
                >
                  {saving ? "Saving…" : saved ? "✓ Saved" : "Save"}
                </button>
              </div>

              {/* Slug preview */}
              {name.trim() && (
                <p style={s.slugPreview}>
                  URL slug:{" "}
                  <code style={s.slugCode}>
                    {name.trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 32)}
                  </code>
                </p>
              )}

              {/* Error */}
              {error && <p style={s.errorMsg}>{error}</p>}

              {/* Suggestions */}
              {suggestions.length > 0 && (
                <div style={s.suggestions}>
                  <p style={s.suggestLabel}>Available alternatives:</p>
                  <div style={s.suggestRow}>
                    {suggestions.map(s => (
                      <button
                        key={s}
                        type="button"
                        onClick={() => pickSuggestion(s)}
                        style={s.suggestBtn}
                      >
                        {s.split("-").map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(" ")}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </form>
          </section>

          <hr style={s.divider} />

          {/* Idea text — read only for now */}
          <section>
            <h2 style={s.sectionTitle}>Business Idea</h2>
            <p style={s.sectionDesc}>Your original idea description (read-only).</p>
            <div style={s.ideaBox}>
              {activeWorkspace?.idea_text || "No idea text saved."}
            </div>
          </section>

          <hr style={s.divider} />

          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <button onClick={() => navigate("/dashboard")} style={s.backBtn}>
              ← Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const s = {
  root: {
    minHeight: "100vh",
    background: "var(--paper,#f5f2eb)",
    fontFamily: "var(--sans,'DM Sans',sans-serif)",
  },
  header: {
    height: 52,
    background: "rgba(245,242,235,0.92)",
    backdropFilter: "blur(10px)",
    borderBottom: "1px solid var(--paper-3)",
    display: "flex",
    alignItems: "center",
    padding: "0 24px",
    gap: 16,
    position: "sticky",
    top: 0,
    zIndex: 50,
  },
  logo: {
    fontFamily: "var(--serif,'Instrument Serif',serif)",
    fontSize: 20,
    color: "var(--ink,#0e0e0d)",
    textDecoration: "none",
    flexShrink: 0,
  },
  breadcrumb: {
    fontSize: 13,
    color: "var(--ink-soft)",
  },
  breadLink: {
    color: "var(--accent,#c8563a)",
    textDecoration: "none",
  },
  body: {
    maxWidth: 640,
    margin: "0 auto",
    padding: "40px 24px",
  },
  card: {
    background: "#fff",
    border: "1px solid var(--paper-3)",
    borderRadius: 12,
    padding: "32px 36px",
  },
  heading: {
    fontFamily: "var(--serif,'Instrument Serif',serif)",
    fontSize: 28,
    fontWeight: 400,
    marginBottom: 6,
    color: "var(--ink,#0e0e0d)",
  },
  sub: {
    fontSize: 14,
    color: "var(--ink-mute)",
    marginBottom: 0,
  },
  divider: {
    border: "none",
    borderTop: "1px solid var(--paper-3)",
    margin: "24px 0",
  },
  sectionTitle: {
    fontSize: 15,
    fontWeight: 600,
    color: "var(--ink,#0e0e0d)",
    marginBottom: 4,
  },
  sectionDesc: {
    fontSize: 13,
    color: "var(--ink-mute)",
    marginBottom: 16,
    lineHeight: 1.5,
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },
  inputRow: {
    display: "flex",
    gap: 10,
    alignItems: "center",
  },
  input: {
    flex: 1,
    padding: "10px 14px",
    fontSize: 14,
    fontFamily: "var(--sans,'DM Sans',sans-serif)",
    border: "1px solid var(--paper-3)",
    borderRadius: 8,
    outline: "none",
    background: "var(--paper,#f5f2eb)",
    color: "var(--ink,#0e0e0d)",
    transition: "border-color 0.2s",
  },
  saveBtn: {
    padding: "10px 20px",
    background: "var(--ink,#0e0e0d)",
    color: "#fff",
    border: "none",
    borderRadius: 8,
    fontSize: 13,
    fontWeight: 500,
    fontFamily: "var(--sans,'DM Sans',sans-serif)",
    transition: "opacity 0.2s",
    flexShrink: 0,
  },
  slugPreview: {
    fontSize: 12,
    color: "var(--ink-mute)",
    marginTop: 2,
  },
  slugCode: {
    fontFamily: "monospace",
    background: "var(--paper-2)",
    padding: "1px 6px",
    borderRadius: 4,
    fontSize: 12,
  },
  errorMsg: {
    fontSize: 13,
    color: "#e07070",
    marginTop: 2,
  },
  suggestions: {
    marginTop: 8,
    padding: "12px 14px",
    background: "rgba(200,86,58,0.04)",
    border: "1px solid rgba(200,86,58,0.15)",
    borderRadius: 8,
  },
  suggestLabel: {
    fontSize: 12,
    color: "var(--ink-mute)",
    marginBottom: 8,
  },
  suggestRow: {
    display: "flex",
    gap: 8,
    flexWrap: "wrap",
  },
  suggestBtn: {
    fontSize: 13,
    fontFamily: "var(--sans,'DM Sans',sans-serif)",
    padding: "6px 14px",
    background: "#fff",
    border: "1px solid rgba(200,86,58,0.3)",
    borderRadius: 20,
    color: "var(--accent,#c8563a)",
    cursor: "pointer",
    transition: "background 0.15s",
  },
  ideaBox: {
    padding: "14px 16px",
    background: "var(--paper,#f5f2eb)",
    border: "1px solid var(--paper-3)",
    borderRadius: 8,
    fontSize: 13,
    color: "var(--ink-soft)",
    lineHeight: 1.65,
  },
  backBtn: {
    fontSize: 13,
    color: "var(--ink-mute)",
    background: "none",
    border: "none",
    cursor: "pointer",
    fontFamily: "var(--sans,'DM Sans',sans-serif)",
    padding: "8px 0",
  },
};